import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const EmailVerification = () => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { checkAuthState } = useAuth();

  // Get email from location state
  const email = location.state?.email;
  const userId = location.state?.userId;

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      if (isSignUpComplete) {
        setIsVerified(true);
        
        // Refresh auth state
        await checkAuthState();
        
        toast({
          title: 'Email Verified!',
          description: 'Your account has been successfully verified.',
          variant: 'success'
        });

        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Account verified successfully! Please sign in.' 
            } 
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      let errorMessage = 'An error occurred during verification.';
      
      if (error.name === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code. Please check and try again.';
      } else if (error.name === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired. Please request a new one.';
      } else if (error.name === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later.';
      }

      setError(errorMessage);
      
      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError('');

    try {
      await resendSignUpCode({ username: email });
      
      setResendCooldown(60); // 60 second cooldown
      
      toast({
        title: 'Code Sent!',
        description: 'A new verification code has been sent to your email.',
        variant: 'success'
      });
    } catch (error) {
      console.error('Resend error:', error);
      
      let errorMessage = 'Failed to resend verification code.';
      
      if (error.name === 'LimitExceededException') {
        errorMessage = 'Too many resend attempts. Please try again later.';
      }

      setError(errorMessage);
      
      toast({
        title: 'Resend Failed',
        description: errorMessage,
        variant: 'error'
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignup = () => {
    navigate('/signup');
  };

  if (isVerified) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
          <p className="text-gray-600">
            Your account has been successfully verified. Redirecting to sign in...
          </p>
        </div>
        
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
        <p className="text-gray-600">
          We&apos;ve sent a verification code to:
        </p>
        <p className="font-medium text-gray-900 mt-1">{email}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleVerification} className="space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            Verification Code
          </label>
          <input
            id="code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-center text-lg tracking-widest"
            placeholder="000000"
            maxLength={6}
            autoComplete="one-time-code"
          />
          <p className="mt-1 text-sm text-gray-500 text-center">
            Enter the 6-digit code from your email
          </p>
        </div>

        <div>
          <Button
            type="submit"
            isLoading={isLoading}
            className="w-full"
            disabled={!code.trim()}
          >
            Verify Email
          </Button>
        </div>
      </form>

      <div className="mt-6 text-center space-y-3">
        <div>
          <p className="text-sm text-gray-600 mb-2">
            Didn&apos;t receive the code?
          </p>
          <Button
            variant="outline"
            onClick={handleResend}
            isLoading={isResending}
            disabled={resendCooldown > 0}
            className="w-full"
          >
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : 'Resend Code'
            }
          </Button>
        </div>

        <div className="pt-3 border-t border-gray-200">
          <button
            onClick={handleBackToSignup}
            className="flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 mx-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Sign Up
          </button>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <Mail className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Check your email
            </h3>
            <div className="mt-1 text-sm text-blue-700">
              <p>
                The verification code may take a few minutes to arrive. 
                Check your spam folder if you don&apos;t see it in your inbox.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
